import React, { useEffect, useState } from "react";
import apiClient from "../../../services/apiClient";
import { getHeaders } from "../../ApiHeaders/apiHeaders";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown"; // Import the reusable Dropdown component

interface IsoData {
  iso_code: string;
  iso_name?: string;
  code_description?: string;
  tz_standard?: string;
  tz_daylight?: string;
  tz_prevailing?: string;
  sys_created_by: string;
  sys_modified_by: string;
  sys_created_date: string;
  sys_modified_date: string;
}

interface ISODropdownProps {
  selectedISO: string;
  onISOChange: (selectedISO: string, selectedISOObject?: IsoData) => void;
  disabled: boolean;
  className?: string;
  onFocus?: () => void;
}

const ISODropdown: React.FC<ISODropdownProps> = ({
  selectedISO,
  onISOChange,
  disabled,
  className = "",
  onFocus,
}) => {
  const [isoData, setIsoData] = useState<IsoData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchIsoData = async () => {
      const startTime = performance.now();
      try {
        const headers = await getHeaders();
        const response = await apiClient.get("/iso/?fetch_mode=Default", {
          headers,
        });

        if (
          response.data.response &&
          Array.isArray(response.data.response.data)
        ) {
          setIsoData(response.data.response.data);
          datadogRum.addAction("Fetched ISOs Successfully", {
            isoCount: response.data.response.data.length,
          });
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        setError("Failed to fetch ISO data");
        console.error("Error fetching ISO data:", err);
        datadogRum.addError("Failed to fetch ISO data", { error: err });
      } finally {
        setLoading(false);
        const endTime = performance.now();
        datadogRum.addAction("ISO API Response Time", {
          duration: endTime - startTime,
        });
      }
    };

    fetchIsoData();
  }, []);

  // Map ISO data to the format expected by the Dropdown component
  const options = isoData.map((iso) => ({
    value: iso.iso_code,
    label: iso.iso_code,
  }));

  const handleISOChange = (selectedValue: string) => {
    const isoObj = isoData.find((item) => item.iso_code === selectedValue);
    onISOChange(selectedValue, isoObj); // Pass both ISO code and object
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedISO}
      onValueChange={handleISOChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder="Select ISO"
      onFocus={onFocus}
    />
  );
};

export default ISODropdown;
